import { CloseOutlined, CopyOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Checkbox, DatePicker, Form, Input, message, Modal, Space } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { strDate2Server } from "@extensions/convert";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./license-order-key.css";

const LicenseOrderKey = ({ companyId, accId, keyId, onClose }) => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [form] = Form.useForm();
  const [keyData, setKeyData] = useState("");
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const user = useSelector(s => s.user);

  const getData = () => {
    if (!keyId) return;
    setLoading(true);
    serverFetch(`license/orderkey?companyId=${companyId}&accId=${accId}&keyId=${keyId}`)
      .then(data => {
        setLoading(false);
        setKeyData(data.body || "");
        setItem(data);
        form.setFieldsValue({
          comment: data.comment || "",
          frozen: data.frozen || false,
          finDate: data.finDate ? dayjs(data.finDate) : null,
        });
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    if (accId && keyId) {
      getData();
    }
  }, [companyId, accId, keyId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(keyData);
      message.success("Ключ скопирован в буфер обмена");
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = keyData;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      message.success("Ключ скопирован в буфер обмена");
    }
  };

  const onValuesChange = changedValues => {
    setItem(prev => ({ ...prev, ...changedValues }));
  };

  const handleSave = () => {
    if (!item) return;

    setLoading(true);
    serverFetch(`license/key`, {
      method: "PUT",
      bodyData: {
        ...item,
        companyId,
        accId,
        id: keyId,
        finDate: item.finDate ? strDate2Server(item.finDate) : null,
      },
    })
      .then(() => {
        setLoading(false);
        message.success("Ключ обновлен");
        onClose(true);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const handleClose = () => {
    setKeyData("");
    setItem(null);
    form.resetFields();
    onClose();
  };

  const titleText = `Ключ лицензии ${companyId} / ${accId} / ${keyId}`;

  const formContent = (
    <>
      <Input.TextArea
        value={keyData}
        readOnly
        rows={6}
        placeholder="Загрузка ключа..."
        style={{ fontFamily: "monospace", fontSize: 12, marginBottom: 16 }}
      />
      <Form form={form} onValuesChange={onValuesChange} layout="vertical" disabled={loading}>
        <Form.Item label="Комментарий" name="comment">
          <Input.TextArea rows={3} placeholder="Введите комментарий" />
        </Form.Item>
        <Form.Item label="Заморожен" name="frozen" valuePropName="checked">
          <Checkbox>Ключ заморожен</Checkbox>
        </Form.Item>
        {user?.admin ? (
          <Form.Item label="Дата окончания" name="finDate">
            <DatePicker format="DD.MM.YYYY" placeholder="Выберите дату окончания" style={{ width: "100%" }} allowClear />
          </Form.Item>
        ) : null}
      </Form>
    </>
  );

  if (isMobile) {
    return createPortal(
      <div
        className="misoft-modal-overlay company-base-overlay--mobile license-order-key-mobile-overlay"
        onClick={e => e.target === e.currentTarget && handleClose()}
      >
        <div className="misoft-modal-content company-base-sheet--mobile" onClick={e => e.stopPropagation()}>
          <div className="company-base-sheet-header">
            <span className="company-base-sheet-title">{titleText}</span>
            <Space size="small" className="company-base-sheet-actions">
              <Button
                type="default"
                icon={<CopyOutlined />}
                onClick={handleCopy}
                disabled={!keyData}
                aria-label="Копировать"
                className="company-base-header-icon-btn"
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                disabled={!item}
                aria-label="Сохранить"
                className="company-base-header-icon-btn"
              />
              <Button
                type="default"
                icon={<CloseOutlined />}
                onClick={handleClose}
                aria-label="Закрыть"
                className="company-base-header-icon-btn"
              />
            </Space>
          </div>
          <div className="company-base-sheet-body">{formContent}</div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <Modal
      title={titleText}
      open
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Закрыть
        </Button>,
        <Button key="copy" type="default" onClick={handleCopy} disabled={!keyData}>
          Копировать
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={loading}>
          Сохранить
        </Button>,
      ]}
      width={600}
      className="cloud-modal-license-order-key"
    >
      <div className="cloud-modal-content">{formContent}</div>
    </Modal>
  );
};

export default LicenseOrderKey;
